import React, { useState, useEffect, useMemo } from "react";

import _ from 'lodash';
import cx from 'classnames';
import moment from "moment";
import Select, { components } from "react-select";
import { Col, Dropdown, Row } from "react-bootstrap";

import { useTransition, animated } from "@react-spring/web";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { OverlayTrigger, Popover } from 'react-bootstrap';
import DatePicker from "react-datepicker"
import { addLearnerToCourse, invalidateAddLearnersState } from "src/redux/actions/coaching/learners";

import Slider from "src/components/slider";
import Actions from "src/components/actionbar";
import InfiniteScroll from "src/components/infiniteScroll";
import { Checkbox } from "src/components/input";
import Thumbnail from "src/components/thumbnail";
import GroupThumbnail from "../groupThumbnail";
import Button from "src/components/button";

import styles from "./learners.module.scss";
import { LearnerPlaceholder } from ".";
import { invalidateLearnerSearchResults, searchLearners } from "src/redux/actions/search";
import { Course, CourseSummary } from "src/types/coaching/courses";
import Tooltip from "src/components/tooltip";
import LinesEllipsis from "react-lines-ellipsis";
import { Simulation, SimulationSummary } from "src/types/coaching/simulation";
import DatePickerComponent from "src/components/datePicker";

interface AddLearnersDialogProps {
  show: boolean;
  course?: Course | Simulation | CourseSummary | SimulationSummary;
  onFinish: () => void;
  onClose: () => void;
}

interface BaseEntity {
  id: string;
  entityType: string;
}

export const AddLearnersDialog: React.FC<AddLearnersDialogProps> = ({ show, course, onFinish, onClose }) => {
  const dispatch = useDispatch();

  const params = useParams();
  const id = params.id || course?.id || "";

  const { loading, learners, currentPage, nextPage, hasMoreItems } = useSelector((state: any) => {
    const learnersState = _.get(state, 'search.learners');
    const loading = _.get(learnersState, 'loading');
    const pagination = _.get(learnersState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const currentPage = _.get(pagination, "currentPage", 0);

    const learners = (_.get(learnersState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      learners: learners,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  const adding = useSelector((state: any) =>
    _.get(state, "learners.add.loading", false),
  );
  const added = useSelector((state: any) =>
    _.get(state, "learners.add.loaded", false),
  );

  const [selectAll, setSelectAll] = useState(false);
  const [selectedLearners, setSelectedLearners] = useState<BaseEntity[]>([]);
  const [query, setQuery] = useState<string | null>("");
  
  const [dueType, setDueType] = useState<"custom" | "assignment">("custom");
  const [dueValue, setDueValue] = useState<Date | number | null>(null);

  const fetchLearnersForPage = (query, page) => {
    const request = {
      query: query || "",
      page: page,
      limit: 20,
    };

    dispatch(searchLearners(request));
  };

  const onSelect = (learner: BaseEntity) => {
    let items = [...selectedLearners];

    const selected = _.find(selectedLearners, { id: learner.id, entityType: learner.entityType });

    if (selected) {
      items = items.filter((item) => !(item.id === selected.id && item.entityType === selected.entityType));
    } else {
      items = [...items, {
        id: learner.id,
        entityType: learner.entityType
      }];
    }

    setSelectedLearners(items);
  };

  const onSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedLearners([]);
  };

  const handleAddLearners = () => {
    let dueDate: string | null = null;

    if (dueValue && dueType === "custom") {
      dueDate = moment(dueValue).endOf("day").toISOString();
    } 
    else if (dueValue && dueType === "assignment" && typeof dueValue === "number") {
      dueDate = moment.utc().add(dueValue, "days").endOf("day").toISOString();
    }  

    const request = {
      id: id,
      allUsers: selectAll,
      learners: selectedLearners,
      dueDate: dueDate,
    };

    dispatch(addLearnerToCourse(request));
  };

  const handleLoadMoreLearners = () => {
    if (nextPage) {
      fetchLearnersForPage(query, nextPage);
    }
  };

  const handleQueryChange = (query: any) => {
    setQuery(query || "");
  };

  const handleClose = () => {
    setSelectedLearners([]);
    setSelectAll(false);
    onClose();
  }
  const formatDate = (date: moment.Moment) =>
    `${date.format("DD MMM YYYY")}   ${date.format("HH:mm A")}, GMT`;
  
  const getDueDateLabel = () => {
    if (dueValue && dueType === "custom" && dueValue instanceof Date) {
      return formatDate(moment(dueValue).endOf("day"));
    }
  
    if (dueType === "assignment" && typeof dueValue === "number") {
      return formatDate(moment().startOf("day").add(dueValue, "days").endOf("day"));
    }
  
    return "Set a date";
  };

  useEffect(() => {
    fetchLearnersForPage(query, 0);
  }, [query]);

  useEffect(() => {
    if (added) {
      dispatch(invalidateAddLearnersState());
      onFinish();
    }
  }, [added]);

  return (
    <Slider.Dialog
      className={styles.addLearner}
      show={show}
      onClose={onClose}
      width={"23.75rem"}
    >
      <Slider.Header
        title={"adding learners"}
        message="Select learners for the course you're creating"
      />
      <Slider.Body className={styles.body}>
        <div className={styles.selectAll}>
          <span className={styles.heading}>
            Assign to all users & user groups
          </span>
          <Checkbox checked={selectAll} onChange={() => onSelectAll()} />
        </div>
        <div className={styles.inputContainer}>
          <Actions.Search query={query} onSearch={handleQueryChange} />
        </div>

        <div className={cx(styles.container)}>
          <InfiniteScroll
            className={styles.learnersList}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={handleLoadMoreLearners}
            loader={() => {
              return _.times(
                _.size(learners) === 0 || currentPage == 0 ? 4 : 1,
                (index) => <LearnerPlaceholder key={index} />,
              );
            }}
          >
            {_.size(learners) === 0 && !loading && (
              <span className={styles.noRecordsFound}>No results found</span>
            )}

            {_.size(learners) > 0 && (
              <LearnersList
                selectAll={selectAll}
                learners={learners}
                selectedLearners={selectedLearners}
                onSelect={onSelect}
              />
            )}
          </InfiniteScroll>
        </div>
        <div className={styles.dueDateWrapper}>
          <span className={styles.dueDateLabel}> 
            <i className="paperflite-calendar" />Due date
          </span>
          <DueDatePickerPopover
            value={dueValue}
            type={dueType}
            onTypeChange={setDueType}
            onChange={setDueValue}
          >
            <span className={styles.pickDateText}>
              {getDueDateLabel()}
            </span>
          </DueDatePickerPopover>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          disabled={adding || (_.size(selectedLearners) === 0 && !selectAll)}
          icon="paperflite-tick"
          onClick={() => handleAddLearners()}
        >
          {adding ? "Adding" : "Add"}
        </Button>
        <Button disabled={adding} variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface LearnersListProps {
  selectAll: boolean;
  learners: any[];
  selectedLearners: any[];
  // eslint-disable-next-line no-unused-vars
  onSelect: (learner: BaseEntity) => void;
}

export const LearnersList: React.FC<LearnersListProps> = ({
  selectAll,
  learners,
  selectedLearners,
  onSelect,
}) => {

  const [showTooltip, setShowTooltip] = useState(false);

  const handleSelect = (learner) => {
    if (!selectAll) {
      onSelect(learner);
    }
  }

  return (
    <>{learners.map((user, index) => {
      const selected = _.find(selectedLearners, { id: user.id, entityType: user.entityType }) != null;
      return (
        <div key={index} className={styles.learner} onClick={() => handleSelect(user)}>
          <div className={styles.details}>
            {user.entityType == "user_group" && (
              <GroupThumbnail
                icons={user.icons}
              />
            )}

            {user.entityType == "user" && (
              <Thumbnail
                media={user.icon}
                defaultImage="/images/assets/default_asset.jpg"
                className={styles.icon}
              />
            )}

          <Tooltip content={user.entityType === `user_group` ? `${user.name}` : `${user.firstName} ${user.lastName}`} placement="top" disabled={!setShowTooltip || user?.name?.length < 20}>
              <LinesEllipsis
                className={styles.name}
                text={user.entityType === `user_group` ? `${user.name}` : `${user.firstName} ${user.lastName}` || ''}
                maxLine="1"
                basedOn="letters"
                ellipsis="..."
                onReflow={({ clamped }) => setShowTooltip(clamped)}
              />
          </Tooltip>
          </div>

          <Checkbox disabled={selectAll} checked={selected || selectAll} onChange={() => onSelect(user)} />
        </div>
      )
    })}
    </>
  );
};

interface LearnersSearchResultProps {
  className?: string;
  query?: string | null;
  selectedLearners: any[];
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const LearnersSearchResult: React.FC<LearnersSearchResultProps> = ({ className, query, selectedLearners, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, learners, currentPage, nextPage, hasMoreItems } = useSelector((state: any) => {
    const learnersState = _.get(state, 'search.learners');
    const loading = _.get(learnersState, 'loading');
    const pagination = _.get(learnersState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const currentPage = _.get(pagination, "currentPage", 0);

    const learners = (_.get(learnersState, 'items') || []);

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      learners: learners,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    searchLearnersForPage(0);

    return () => {
      dispatch(invalidateLearnerSearchResults());
    }
  }, [query]);

  const searchLearnersForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        page: page,
        limit: 20
      };

      dispatch(searchLearners(request));
    }
  }

  const handleLoadMore = () => {
    if (nextPage) {
      searchLearnersForPage(nextPage);
    }
  }

  return (
    <InfiniteScroll className={cx(styles.searchResults, className)}
      loading={loading}
      hasMore={hasMoreItems}
      loadMore={handleLoadMore}
      loader={() => {
        return _.times(_.size(learners) === 0 || currentPage == 0 ? 4 : 1, (index) => (
          <LearnerPlaceholder key={index} />
        ));
      }}
    >
      {_.size(learners) === 0 && !loading && (
        <span className={styles.noRecordsFound}>No results found</span>
      )}

      {_.size(learners) > 0 &&
        <LearnersList
          selectAll={false}
          learners={learners}
          selectedLearners={selectedLearners}
          onSelect={onSelection}
        />
      }
    </InfiniteScroll>
  );
}

const ASSIGNMENT_OPTIONS = [
  { label: "1 day", value: 1 },
  { label: "3 days", value: 3 },
  { label: "1 week", value: 7 },
  { label: "2 weeks", value: 14 },
  { label: "1 month", value: 30 },
  { label: "2 months", value: 60 },
];

interface DueDatePickerPopoverProps {
  value: any;
  type: "custom" | "assignment";
  onTypeChange: (type: "custom" | "assignment") => void;
  onChange: (date: any) => void;
  children: React.ReactNode;
}

export const DueDatePickerPopover: React.FC<DueDatePickerPopoverProps> = ({
  value,
  type,
  onTypeChange,
  onChange,
  children,
}) => {
  const [selectedValue, setSelectedValue] = useState(type);
  const [errorMessage, setErrorMessage] = useState("");
  const [dueDateType, setDueDateType] = React.useState({
    value: "custom",
    label: "Pick a date",
  });
  const [showPopover, setShowPopover] = useState(false);

  const dueDateOptions = [
    {
      value: "custom",
      label: "Pick a date",
    },
    {
      value: "assignment",
      label: "Days from assignment",
    }
  ];

  const handleDateChange = (date: Date | null) => {
    if (date) {
      onChange(date);
      setShowPopover(false);
    }
  };

  const handleViewerTypeChange = (value: { value: string; label: string }) => {
    setDueDateType(value);
    setSelectedValue(value.value as "custom" | "assignment");
    setErrorMessage("");
    onTypeChange(value.value as "custom" | "assignment");
    if (value.value === "custom") {
      onChange(null);
    }
  };


  const handleClear = () => {
    setDueDateType({ value: "custom", label: "Custom Date" });
    setSelectedValue("custom");
    onTypeChange("custom");
    onChange(null);
  };

  const handleAssessmentOption = (selectedValue) => {
    onChange(value === selectedValue ? null : selectedValue);
    setShowPopover(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const days = Number(value);
    setErrorMessage(""); 

    if (!value || days <= 0) {
      onChange("");
      return;
    }

    if (value.includes(".") || !Number.isInteger(Number(value))) {
      setErrorMessage("Decimal values are not supported");
      onChange("");
      return;
    }

    if (days > 365) {
      setErrorMessage("Only upto 1 year is allowed");
      onChange("");
      return;
    }
    onChange(days);
  };

  const renderContent = () => (
    <Popover id="popover-due-date" className={styles.dueDatePopover}>
      <Popover.Body>
        <div className={styles.filterContainer}>
          <DropDown
            selected={dueDateType}
            onChange={handleViewerTypeChange}
            options={dueDateOptions}
          />
        </div>
        
        {selectedValue === "custom" && (
          <div className={styles.datePickerContainer}>
            <DatePicker
              selected={value as Date}
              onChange={handleDateChange}
              dateFormat="dd MMM yyyy"
              minDate={new Date()}
              maxDate={moment().add(1, "years").toDate()}
              showYearDropdown
              scrollableYearDropdown
              inline
            />
          </div>
        )}

        {selectedValue === "assignment" && (
          <div className={styles.relativeOptions}>
            <div className={styles.customDaysInput}>
              <input
                type="number"
                min={1}
                value={typeof value === "number" ? value : ""}
                placeholder="custom days"
                onKeyDown={(e) => {
                  if (e.key === "-") e.preventDefault();
                }}
                onChange={handleChange}
              />
              {errorMessage && (
                <span className={styles.errorMessage}>
                  <i className="paperflite-danger" />
                  {errorMessage}
                </span>
              )}
            </div>

            {ASSIGNMENT_OPTIONS.map((option) => {
              const active = value === option.value;
              return (
                <div
                  key={option.value}
                  className={cx(styles.optionButton, {
                    [styles.active]: active,
                  })}
                  onClick={() => handleAssessmentOption(option.value)} 
                >
                  <i
                    className={
                      active
                        ? "fa-solid fa-circle-check"
                        : "fa-regular fa-circle"
                    }
                  />
                  <span className={styles.label}>{option.label}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className={styles.clearWrapper}>
          <div className={styles.clearButton} onClick={handleClear}>
            clear
          </div>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="top-end"
      overlay={renderContent()}
      show={showPopover}
      onToggle={(show) => {
        setShowPopover(show);
        if (!show) {
          setErrorMessage("");
        }
      }}
      rootClose
    >
      {React.isValidElement(children) ? children : <span>{children}</span>}
    </OverlayTrigger>
  );
};


interface DropDownProps {
  selected: {
    value: string;
    label: string;
  };
  options: Array<{ value: string; label: string; icon?: string }>;
  onChange: ({ value, label }: { value: string; label: string }) => void;
}
export const DropDown: React.FC<DropDownProps> = ({
  selected,
  options,
  onChange,
}) => {
  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === selected.value);
  }, [selected, options]);

  const [show, setShow] = React.useState(false);

  const handleSelect = (value: string) => {
    if (onChange) {
      onChange({
        value,
        label: options.find((option) => option.value === value)?.label || "",
      });
    }
  };

  return (
    <Dropdown className={styles.dueDateDropdown}>
      <span onClick={() => setShow(!show)}>
        <Dropdown.Toggle>
          {selectedOption && <>{selectedOption?.label}</>}
          {_.size(options) > 1 && (
            <i
              className={cx("paperflite-dropdown", {
                [styles.openModal]: show,
              })}
            ></i>
          )}
        </Dropdown.Toggle>
      </span>

      {_.size(options) > 1 && (
        <Dropdown.Menu
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 6],
                },
              },
            ],
          }}
        >
          {options.map((option, index) => {
            const selected = option.value === selectedOption?.value;
            return (
              <Dropdown.Item
                key={index}
                className={cx({ [styles.active]: selected })}
                onClick={() => handleSelect(option.value)}
                >
                <span>{option.label}</span>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};
